
import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronRight, ArrowLeft, BrainCircuit } from 'lucide-react';
import { NAV_ITEMS } from '../constants';
import AstraLogo from './AstraLogo';
import { ViewType } from '../App';

interface NavbarProps {
  isScrolled: boolean;
  currentView: ViewType;
  onNavigate: (view: ViewType, sectionId?: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ isScrolled, currentView, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const handleLinkClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    
    if (id === 'services' && currentView !== 'services') {
        onNavigate('services');
    } else if (id === 'intelligence') {
        onNavigate('intelligence');
    } else if (currentView !== 'home') {
        onNavigate('home', id);
    } else {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        setActiveSection(id);
      }
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (currentView !== 'home') {
        setActiveSection(currentView);
        return;
    }
    const handleScroll = () => {
      const sections = NAV_ITEMS.map(item => item.href.replace('#', ''));
      const scrollPosition = window.scrollY + 150;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          if (
            element.offsetTop <= scrollPosition &&
            element.offsetTop + element.offsetHeight > scrollPosition
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentView]);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-700 ${
        isScrolled 
          ? 'py-3 bg-black/40 backdrop-blur-2xl border-b border-white/5' 
          : 'py-8 bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <button 
          onClick={() => onNavigate('home')} 
          className="flex items-center gap-4 group focus:outline-none"
        >
          <AstraLogo size={48} />
          <span className="text-xl font-extrabold tracking-tight flex flex-col text-left leading-none">
            <span className="text-white tracking-[-0.03em] group-hover:text-brand-500 transition-colors">SENTRIX ASTRA</span>
            <span className="text-brand-400 text-[11px] font-mono tracking-[0.2em] mt-0.5 opacity-80 uppercase">Technologies</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-10">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => handleLinkClick(e, item.href)}
              className={`group relative text-[13px] font-semibold transition-colors uppercase tracking-[0.12em] ${
                activeSection === item.href.replace('#', '') ? 'text-brand-400' : 'text-slate-400 hover:text-white'
              }`}
            >
              {item.label}
              <span className={`absolute -bottom-1 left-0 h-px bg-brand-400 transition-all duration-300 ${
                activeSection === item.href.replace('#', '') ? 'w-full opacity-100' : 'w-0 opacity-60 group-hover:w-full'
              }`} />
            </a>
          ))}

          {/* New Intelligence Nav Item */}
          <a
            href="#intelligence"
            onClick={(e) => handleLinkClick(e, '#intelligence')}
            className={`group relative text-[13px] font-semibold transition-colors uppercase tracking-[0.12em] ${
              activeSection === 'intelligence' ? 'text-brand-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            Intelligence
            <span className={`absolute -bottom-1 left-0 h-px bg-brand-400 transition-all duration-300 ${
              activeSection === 'intelligence' ? 'w-full opacity-100' : 'w-0 opacity-60 group-hover:w-full'
            }`} />
          </a>
          
          {currentView !== 'home' ? (
             <button
                onClick={() => onNavigate('home')}
                className="group px-6 py-2.5 rounded-full glass-premium border border-white/10 text-white text-[11px] font-bold transition-all hover:bg-white/5 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                RETURN TO CORE
              </button>
          ) : (
              <button
                onClick={(e) => handleLinkClick(e, '#contact')}
                className="group px-6 py-2.5 rounded-full bg-brand-500 text-black text-[13px] font-bold transition-all hover:bg-brand-400 hover:scale-105 active:scale-95 flex items-center gap-2"
              >
                INQUIRE
                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
          )}
        </div>

        <button 
          onClick={() => setIsOpen(!isOpen)} 
          className="lg:hidden w-10 h-10 rounded-lg flex items-center justify-center text-white bg-white/5 border border-white/10"
        >
          {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <div className={`lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-3xl border-t border-white/5 transition-all duration-500 overflow-hidden ${isOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-8 flex flex-col gap-6">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.label}
              onClick={(e) => handleLinkClick(e, item.href)}
              className={`text-xl font-bold text-left transition-colors ${
                activeSection === item.href.replace('#', '') ? 'text-brand-400' : 'text-slate-300 hover:text-brand-400'
              }`}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => onNavigate('intelligence')}
            className={`text-xl font-bold text-left transition-colors ${
                activeSection === 'intelligence' ? 'text-brand-400' : 'text-slate-300 hover:text-brand-400'
            }`}
          >
            Intelligence
          </button>
          <button
            onClick={() => onNavigate(currentView === 'home' ? 'services' : 'home')}
            className="mt-4 px-8 py-4 rounded-xl bg-brand-500 text-black font-bold text-center"
          >
            {currentView === 'home' ? 'VIEW PROTOCOLS' : 'BACK TO CORE'}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
